import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, setToken, setUnauthorizedHandler } from "../lib/api";
import type { Admin, SignInResult } from "../lib/types";

interface AuthState {
  admin: Admin | null;
  /** True until the boot-time /auth/me has answered. */
  loading: boolean;
  /**
   * Step one. Returns what happens NEXT rather than a session, because a
   * correct password is no longer enough to be signed in — see the note on
   * `adminLogin` in the API. `step: "otp"` means a code is on its way to the
   * admin's phone and `verifyOtp` finishes the job.
   */
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Step two. This is what actually produces a session. */
  verifyOtp: (challenge: string, code: string) => Promise<void>;
  /** Texts the code again. The challenge stays the same one. */
  resendOtp: (challenge: string) => Promise<{ phoneHint: string }>;
  /**
   * "I've forgotten my password." Rejects with a message naming the problem —
   * no such account, wrong domain, no mobile on file — so a typo fails on the
   * email screen rather than at a code box waiting for an SMS.
   */
  forgotPassword: (
    email: string,
  ) => Promise<{ challenge: string; phoneHint: string }>;
  /** Sets the new password once the texted code checks out, and signs in. */
  resetPassword: (
    challenge: string,
    code: string,
    password: string,
    confirmPassword: string,
  ) => Promise<{ sessionsEnded: number }>;
  /**
   * Adopt a session established outside this provider — the invite page, which
   * signs its own accept response in. There is no `register`: an account is
   * created by an OWNER's invitation or seeded from the command line, never
   * from the browser.
   */
  adopt: (admin: Admin) => void;
  signOut: () => Promise<void>;
  /** OWNER and ADMIN may revoke sessions; ANALYST is read-only. */
  canRevoke: boolean;
  isOwner: boolean;
}

const Context = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  // Any 401 anywhere in the app drops the session here, so a token that
  // expires mid-session lands on the login screen instead of on a page of
  // empty panels the user cannot explain.
  useEffect(() => {
    setUnauthorizedHandler(() => setAdmin(null));
  }, []);

  // Boot: the cookie alone may carry a session on a same-site deploy, so this
  // asks the server rather than trusting the absence of a stored token.
  useEffect(() => {
    let cancelled = false;

    api<{ admin: Admin | null }>("/api/admin/auth/me")
      .then((res) => {
        if (!cancelled) setAdmin(res.admin);
      })
      .catch(() => {
        if (!cancelled) setAdmin(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      const res = await api<
        | { step: "otp"; challenge: string; phoneHint: string; expiresAt: string }
        | { step: "done"; token: string; admin: Admin }
      >("/api/admin/auth/login", { method: "POST", body: { email, password } });

      // Branching on `step` rather than on the presence of `token`, so a
      // response shape that changes shape cannot silently turn a challenge
      // into a sign-in.
      if (res.step === "done") {
        setToken(res.token);
        setAdmin(res.admin);
        return { step: "done" };
      }

      return res;
    },
    [],
  );

  const verifyOtp = useCallback(async (challenge: string, code: string) => {
    const res = await api<{ token: string; admin: Admin }>(
      "/api/admin/auth/verify-otp",
      { method: "POST", body: { challenge, code } },
    );
    setToken(res.token);
    setAdmin(res.admin);
  }, []);

  const resendOtp = useCallback(async (challenge: string) => {
    return api<{ phoneHint: string }>("/api/admin/auth/resend-otp", {
      method: "POST",
      body: { challenge },
    });
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    return api<{ challenge: string; phoneHint: string }>(
      "/api/admin/auth/forgot",
      { method: "POST", body: { email } },
    );
  }, []);

  const resetPassword = useCallback(
    async (
      challenge: string,
      code: string,
      password: string,
      confirmPassword: string,
    ) => {
      const res = await api<{
        token: string;
        admin: Admin;
        sessionsEnded: number;
      }>("/api/admin/auth/reset-password", {
        method: "POST",
        body: { challenge, code, password, confirmPassword },
      });

      // Signed straight in: the texted code proved the phone and they have
      // just chosen the password, so both factors are satisfied.
      setToken(res.token);
      setAdmin(res.admin);
      return { sessionsEnded: res.sessionsEnded };
    },
    [],
  );

  const adopt = useCallback((next: Admin) => setAdmin(next), []);

  const signOut = useCallback(async () => {
    // The revoke is what actually ends the session — clearing local state
    // alone would leave a working token behind. A failed call still signs the
    // user out locally: they asked to leave, and the session expires anyway.
    await api("/api/admin/auth/logout", { method: "POST" }).catch(() => {});
    setToken(null);
    setAdmin(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      admin,
      loading,
      signIn,
      verifyOtp,
      resendOtp,
      forgotPassword,
      resetPassword,
      adopt,
      signOut,
      canRevoke: admin?.role === "OWNER" || admin?.role === "ADMIN",
      isOwner: admin?.role === "OWNER",
    }),
    [
      admin,
      loading,
      signIn,
      verifyOtp,
      resendOtp,
      forgotPassword,
      resetPassword,
      adopt,
      signOut,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
